import { Color } from '@billy/design-system'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ReactComponent as ImageTopVendorsDKComponent } from '../../../../assets/images/dashboard-empty-state/dk/top-vendors.svg'
import { ReactComponent as ImageTopVendorsENComponent } from '../../../../assets/images/dashboard-empty-state/en/top-vendors.svg'
import { ReactComponent as ImageTopVendorsFRComponent } from '../../../../assets/images/dashboard-empty-state/fr/top-vendors.svg'
import { LocaleCode } from '../../../../enums/localeCode'
import { QueryKeys } from '../../../../enums/queryKeys'
import { useLocale } from '../../../../hooks/useLocale'
import { EmptyStateImage } from '../../elements/EmptyStateImage'
import { Widget } from '../../elements/Widget'
import { useFiscalYearQueryParams } from '../../hooks/useFiscalYearQueryParams'
import { fetchTopSuppliers, StatsDataQueryParams } from '../../services/query-api'
import { getUsersWithColors } from '../../utils/getUsersWithColors'
import { UsersList, UsersListContext } from '../UsersList'

const emptyImageByLanguage: Record<LocaleCode, ReactElement> = {
  [LocaleCode.DA]: <ImageTopVendorsDKComponent />,
  [LocaleCode.EN_US]: <ImageTopVendorsENComponent />,
  [LocaleCode.FR]: <ImageTopVendorsFRComponent />,
}

export const TopVendors = (): ReactElement => {
  const { t } = useTranslation()
  const { code = LocaleCode.EN_US } = useLocale()
  const { organization } = useUserOrganization()
  const { queryParams } = useFiscalYearQueryParams(organization?.id)

  const localeCode = code as LocaleCode

  const { data: topSuppliers, isLoading: isTopSuppliersLoading } = useQuery(
    [QueryKeys.TopSuppliers, organization?.id, queryParams],
    () => fetchTopSuppliers(organization?.id || '', undefined, queryParams || ({} as StatsDataQueryParams)),
    {
      enabled: !!organization && !!queryParams,
      refetchOnMount: 'always',
    },
  )
  const colors = useMemo(() => [Color.Lavender200, Color.Purple500, Color.Yellow400], [])
  const topSuppliersWithColor = getUsersWithColors(topSuppliers?.data || [], colors)
  const isLoading = !organization || isTopSuppliersLoading

  const doTopSuppliersExist = topSuppliersWithColor.length > 0
  const shouldShowEmptyState = !!organization && !doTopSuppliersExist && !isLoading

  return (
    <Widget title={t('dashboard.widget.topVendors.title')}>
      {shouldShowEmptyState && (
        <EmptyStateImage image={emptyImageByLanguage[localeCode] ?? emptyImageByLanguage[LocaleCode.EN_US]} />
      )}
      {doTopSuppliersExist && (
        <UsersList usersListContext={UsersListContext.Vendor} isLoading={isLoading} users={topSuppliersWithColor} />
      )}
    </Widget>
  )
}
